import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { ControlValueAccessor } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as i18nIsoCountries from 'i18n-iso-countries';
import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import { BehaviorSubject } from 'rxjs';
import { InputType } from '../../../models/input-type.model';
import { DropdownItem } from '../dropdown/dropdown.component';

@UntilDestroy()
@Component({
  selector: 'lib-text-input-phone',
  templateUrl: './text-input-phone.component.html',
  styleUrls: ['./text-input-phone.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TextInputPhoneComponent implements OnInit, ControlValueAccessor {
  countryCodes: DropdownItem[];
  inputType = InputType.Phone;
  _phoneNumber: string;
  isDisabled: boolean;
  selectedCountry$ = new BehaviorSubject(undefined);

  @Output() phoneFocus = new EventEmitter<void>();

  @Input() set error(value: boolean) {
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        const comboBox = document.querySelector('.combined-box');
        if (comboBox) {
          const dropdown = document.querySelector('.ng-select-container');
          const input = document.querySelector('.phone-number-input');
          if (value) {
            input?.classList.add('invalid');
            dropdown?.classList.add('invalid');
            comboBox?.classList.add('invalid');
          } else {
            input?.classList.remove('invalid');
            dropdown?.classList.remove('invalid');
            comboBox?.classList.remove('invalid');
          }
        }
      });
    });
  }

  @Input() set disabled(value: boolean) {
    this.setDisabledState(value);
  }

  @Input() id: string;

  set selectedCountry(value: CountryCode) {
    this.selectedCountry$.next(value);
    if (value) {
      this.zone.runOutsideAngular(() => {
        setTimeout(() => {
          this.setInputSize();
        });
      });
    }
  }

  get selectedCountry() {
    return this.selectedCountry$.value;
  }

  set phoneNumber(value) {
    this._phoneNumber = value;
  }

  get phoneNumber() {
    return this._phoneNumber;
  }

  constructor(
    private translocoService: TranslocoService,
    public elementRef: ElementRef,
    public ngControl: NgControl,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
  ) {
    ngControl.valueAccessor = this;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange = (_value: string) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  writeValue(value): void {
    this.phoneNumber = value;
    this.cdr.detectChanges();
  }

  registerOnChange(fn: (value) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  selectedCountryChange($event) {
    this.numberAndCountryChange(this.phoneNumber, $event);
  }

  phoneNumberInputChange($event) {
    this.numberAndCountryChange($event, this.selectedCountry);
  }

  numberAndCountryChange(pN, sC) {
    try {
      const phoneNumber = parsePhoneNumberWithError(pN, sC);
      const value = phoneNumber.isValid() ? phoneNumber.format('E.164') : pN;
      this.selectedCountry = sC;
      this.onChange(value);
    } catch (err) {
      if (err.message === 'NOT_A_NUMBER') {
        this.onChange(undefined);
      }
    }
  }

  initSelectedCountry() {
    if (this.phoneNumber) {
      try {
        const phoneNumber = parsePhoneNumberWithError(this.phoneNumber);
        this.selectedCountry = phoneNumber.country;
      } catch {
        this.selectedCountry = undefined;
      }
    }

    if (!this.selectedCountry) {
      this.selectedCountry =
        this.translocoService.getActiveLang() === 'sv' ? 'SE' : 'GB';
    }
  }

  ngOnInit(): void {
    this.translocoService.langChanges$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.countryCodes = getCountries().map((value) => {
          return {
            value: value,
            label:
              i18nIsoCountries.getName(
                value,
                this.translocoService.getActiveLang(),
              ) +
              ' (+' +
              getCountryCallingCode(value).toString() +
              ')',
            selectedLabel: '+' + getCountryCallingCode(value).toString(),
          };
        });

        this.countryCodes.sort((a, b) => {
          const labelA = a.label.toUpperCase();
          const labelB = b.label.toUpperCase();
          if (labelA < labelB) {
            return -1;
          }
          if (labelA > labelB) {
            return 1;
          }
          return 0;
        });

        //Remove countries without i18n coverage
        this.countryCodes = this.countryCodes.filter((value) => {
          return value.label.split(' ')[0] !== 'undefined';
        });
      });

    this.initSelectedCountry();

    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.setInputSize();
      });
    });
  }

  onFocus() {
    this.phoneFocus.emit();
  }

  onBlur() {
    this.onTouched();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  hasError() {}

  private setInputSize() {
    const border = this.elementRef.nativeElement.querySelector(
      '.phone-input-border',
    );
    const inputComponent =
      this.elementRef.nativeElement.querySelector('.text-input-phone');
    if (!inputComponent) {
      return;
    }
    let dropdownWidth;

    try {
      const dropdownComponent = this.elementRef.nativeElement
        .querySelector('.ng-value-label, .mobile-dropdown-label')
        .cloneNode(true);
      document.body.append(dropdownComponent);
      dropdownWidth = dropdownComponent.offsetWidth;
      dropdownComponent.remove();
    } catch {
      dropdownWidth = 0;
    }

    inputComponent.style.marginLeft = `${(dropdownWidth + 64) / 16}rem`;
    border.style.left = `${(dropdownWidth + 66) / 16}rem`;
  }
}
